"use client";

import { Loader2, Sparkles } from "lucide-react";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";

import { apiUrl } from "@/shared/api/config";
import { useDesktopToken } from "@/shared/lib/hooks/use-desktop-token";
import { Button } from "@/shared/ui/buttons/button";

export interface CreateLobbyResult {
  success: boolean;
  error: string | null;
  invitedCount: number;
  failedInvites: string[];
  needFriend: string[];
}

interface CreateLobbyButtonProps {
  matchId: string;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "outline";
  fullWidth?: boolean;
  className?: string;
  onResult?: (result: CreateLobbyResult) => void;
  /**
   * Может ли текущий пользователь создавать лобби. False → кнопка скрыта.
   * Хост — капитан Participant1 (выше по сетке). Заполняется из
   * GET /api/matches/{id}/players → currentUserIsHost.
   */
  isHost?: boolean;
  /**
   * Заблокировать кнопку (с tooltip-причиной). Например, во время игры в LCU.
   */
  disabledReason?: string | null;
}

export function CreateLobbyButton({
  matchId,
  size = "default",
  variant = "default",
  fullWidth = false,
  className,
  onResult,
  isHost = true,
  disabledReason,
}: CreateLobbyButtonProps) {
  const t = useTranslations("Match.createLobby");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<CreateLobbyResult | null>(null);
  const { getToken: getDesktopToken } = useDesktopToken();

  const handleClick = useCallback(async () => {
    if (!matchId || creating) return;
    setCreating(true);
    setResult(null);

    try {
      const { invokeTauri } = await import("@/shared/lib/tauri");
      // Tauri reqwest НЕ разделяет cookie-jar с webview → нужен явный JWT.
      // useDesktopToken загружает токен через session-auth (sid cookie).
      const authToken = (await getDesktopToken()) ?? "";
      if (!authToken) {
        const errResult: CreateLobbyResult = {
          success: false,
          error: t("resultFailed"),
          invitedCount: 0,
          failedInvites: [],
          needFriend: [],
        };
        setResult(errResult);
        onResult?.(errResult);
        setCreating(false);
        return;
      }

      const res = await invokeTauri<CreateLobbyResult>(
        "create_tournament_lobby",
        {
          req: {
            matchId,
            authToken,
            backendUrl:
              typeof window !== "undefined"
                ? window.location.origin
                : apiUrl,
          },
        },
      );
      setResult(res);
      onResult?.(res);
    } catch (e) {
      const errResult: CreateLobbyResult = {
        success: false,
        error: e instanceof Error ? e.message : t("resultFailed"),
        invitedCount: 0,
        failedInvites: [],
        needFriend: [],
      };
      setResult(errResult);
      onResult?.(errResult);
    } finally {
      setCreating(false);
    }
  }, [matchId, creating, onResult, t, getDesktopToken]);

  const widthClass = fullWidth ? "w-full" : "";

  // Если юзер не хост — не показываем кнопку (бэк всё равно не отдаст лобби-имя
  // не-капитану нет смысла, но в принципе можно: пусть только хост видит).
  if (!isHost) return null;

  const isDisabled = creating || Boolean(disabledReason);

  return (
    <div className={`space-y-2 ${className ?? ""}`.trim()}>
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={handleClick}
        disabled={isDisabled}
        title={disabledReason ?? undefined}
        className={widthClass}
      >
        {creating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t("creating")}
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-4 w-4" />
            {t("cta")}
          </>
        )}
      </Button>
      {disabledReason && !creating && (
        <p className="text-muted-foreground text-xs">{disabledReason}</p>
      )}

      {result && (
        <div
          className={`rounded-md border p-3 text-sm ${
            result.success
              ? "border-primary/40 bg-primary/5 text-foreground"
              : "border-destructive/40 bg-destructive/5 text-foreground"
          }`}
        >
          {result.success ? (
            <div className="space-y-1">
              <p className="font-medium">
                {t("resultSuccess", { count: result.invitedCount })}
              </p>
              {result.failedInvites.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  {t("failedInvites", {
                    names: result.failedInvites.join(", "),
                  })}
                </p>
              )}
              {result.needFriend.length > 0 && (
                <p className="text-muted-foreground text-xs">
                  {t("needFriendHint", {
                    names: result.needFriend.join(", "),
                  })}
                </p>
              )}
            </div>
          ) : (
            <p>{result.error ?? t("resultFailed")}</p>
          )}
        </div>
      )}
    </div>
  );
}
